<template>
  <ContextMenu
    tag="span"
    class="context-menu"
    :handler-class="refClass"
    :active="isOpened"
    :offset-y="5"
    :offset-x="0"
    @update:active="(value) => (isOpened = value)"
  >
    <TheContent
      v-for="item of children"
      :key="item.id"
      :elem="item"
    />

    <template #context>
      <div class="ref-desktop-content">
        <component
          :is="refComponent"
          :href="url"
          :target="target"
          >{{ content }}</component
        >
        <SvgAsset
          class="close"
          :path="closeSvg"
          @click="isOpened = false"
        />
      </div>
    </template>
    <template #mobile>
      <div class="ref-mobile-content">
        <component
          :is="refComponent"
          :href="url"
          :target="target"
          >{{ content }}</component
        >
      </div>
    </template>
  </ContextMenu>
</template>

<script lang="ts">
  import type { Component, PropType } from 'vue';
  import { vOnClickOutside } from '@vueuse/components';
  import TheContent from '~/components/TheContent';
  import { isHomeLink } from '~/utils/link';
  import type { IRef } from '~/types/components/TheRef';
  import ContextMenu from '~/components/ContextMenu.vue';
  import closeSvg from '@/assets/images/svg/cross--mobile.svg';
  import SvgAsset from '@devhacker/shared/components/SvgAsset.vue';

  export default defineNuxtComponent({
    name: 'TheRef',
    components: {
      TheContent,
      ContextMenu,
      SvgAsset,
    },

    directives: {
      clickOutside: vOnClickOutside,
    },

    props: {
      elem: {
        type: Object as PropType<IRef>,
        required: true,
      },
    },

    data: () => ({
      isOpened: false,
      popup: {} as Component,
    }),

    computed: {
      closeSvg: () => closeSvg,

      children() {
        return this.elem.children;
      },

      attributes() {
        return this.elem.attributes;
      },

      url() {
        return this.attributes.url;
      },

      content() {
        return this.attributes.content;
      },

      target() {
        return this.isInternal ? null : '_blank';
      },

      refComponent() {
        return this.url ? 'a' : 'span';
      },

      isInternal() {
        return isHomeLink(this.url);
      },

      refClass() {
        return {
          ref: true,
          [`ref--active`]: this.isOpened,
        };
      },
    },
  });
</script>

<style lang="scss" scoped>
  .context-menu {
    :deep(.ref) {
      display: inline;
      margin: 0 1px;
      border-bottom: 1px dashed $border-color--1;
      cursor: pointer;

      @include hover {
        border-color: $hover-color--1;
        color: $hover-color--1;
      }
    }

    :deep(.ref--active) {
      color: $active-color--1;
    }
  }

  .ref-desktop-content {
    padding: $spacing-4 $spacing-14 $spacing-4 $spacing-5;
    font-family: $mainFont;
    line-height: 24px;

    :deep(a) {
      @include link-styles;
    }

    .close {
      z-index: 1;
      width: 24px;
      height: 24px;
      position: absolute;
      top: 16px;
      right: 20px;
      cursor: pointer;
    }
  }

  .ref-mobile-content {
    font-family: $text-font--1;
    padding: $spacing-4;

    a {
      @include link-styles;
    }
  }
</style>
